import api from './api';
import { apiUrl, aiUrl } from './config';
import { getAuthHeaders } from './auth';
import type { ChatSession, ChatMessage } from '@/types';

export interface ToolCallInfo {
  type: string;  // web_search, code_interpreter, file_search, image_generation
  id?: string;
  status?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
}

export interface StreamMessage {
  type: 'content' | 'done' | 'error' | 'tool_call' | 'tool_result' | 'thinking' | 'start' | 'artifact_created';
  content?: string;
  error?: string;
  toolName?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toolData?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toolResult?: any;
  thinking?: string;  // Extended thinking / reasoning content
  metadata?: {
    provider?: string;
    model?: string;
    tool_calls?: ToolCallInfo[];
    mode?: 'single_agent' | 'multi_agent';
    agent?: string;
    // Artifact-related metadata
    artifact_id?: string;
    artifact_name?: string;
    artifact_type?: string;
    file_path?: string;
  };
}

// Multimodal content types
export interface TextContent {
  type: 'text';
  text: string;
}

export interface ImageContent {
  type: 'image_url';
  image_url: {
    url: string; // base64 data URL or HTTP URL
  };
}

export type MessageContent = string | (TextContent | ImageContent)[];

export interface MultimodalMessage {
  role: string;
  content: MessageContent;
}

export interface UserContext {
  region?: string;
  language?: string;
  name?: string;
  llmPreset?: string;
}

// Backend API response types (JSONAPI format)
interface BackendSessionResponse {
  id: string;
  type: string;
  attributes: {
    id: string;
    title: string;
    status: string;
    metadata: Record<string, unknown>;
    context: Record<string, unknown>;
    created_at: string;
    updated_at: string;
    message_count: number;
    total_tokens: number;
    last_message_at: string | null;
    last_message_preview: string | null;
  };
  relationships?: {
    messages?: {
      data: Array<{ id: string; type: string }>;
    };
  };
}

interface BackendMessageResponse {
  id: string;
  type: string;
  attributes: {
    id: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    status: string;
    metadata: Record<string, unknown>;
    token_count: number;
    model: string | null;
    cost: number | null;
    created_at: string;
    updated_at: string;
    word_count: number;
    is_reply: boolean;
    reply_count: number;
    parent_message_id: string | null;
  };
}

interface BackendSessionWithIncluded {
  data: BackendSessionResponse;
  included?: BackendMessageResponse[];
}

// Transform backend session to frontend format
function transformSession(backendSession: BackendSessionResponse, messages: ChatMessage[] = []): ChatSession {
  return {
    id: backendSession.id,
    projectId: undefined, // Will be set by caller if needed
    title: backendSession.attributes.title,
    messages,
    createdAt: backendSession.attributes.created_at,
    updatedAt: backendSession.attributes.updated_at,
  };
}

// Transform backend message to frontend format
function transformMessage(backendMessage: BackendMessageResponse): ChatMessage {
  return {
    id: backendMessage.id,
    projectId: undefined,
    userId: backendMessage.attributes.role === 'user' ? 'user' : 'assistant',
    content: backendMessage.attributes.content,
    role: backendMessage.attributes.role,
    createdAt: backendMessage.attributes.created_at,
  };
}

export const chatService = {
  // GET /api/v1/projects/:project_id/chat_sessions
  async getSessions(projectId: string): Promise<ChatSession[]> {
    const response = await api.get<{ success: boolean; data: BackendSessionResponse[]; meta: { current_page: number; total_pages: number; total_count: number } }>(
      `/projects/${projectId}/chat_sessions`
    );
    return response.data.data.map(session => ({
      ...transformSession(session),
      projectId,
    }));
  },

  // GET /api/v1/projects/:project_id/chat_sessions/:id (with messages)
  async getSession(projectId: string, sessionId: string): Promise<ChatSession> {
    const response = await api.get<{ success: boolean; data: BackendSessionWithIncluded['data']; included?: BackendMessageResponse[] }>(
      `/projects/${projectId}/chat_sessions/${sessionId}`
    );

    // Extract messages from included array if present
    const messages: ChatMessage[] = [];
    if (response.data.included) {
      for (const item of response.data.included) {
        if (item.type === 'message') {
          messages.push({
            ...transformMessage(item),
            projectId,
          });
        }
      }
      // Sort by createdAt
      messages.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    }

    return {
      ...transformSession(response.data.data, messages),
      projectId,
    };
  },

  // POST /api/v1/projects/:project_id/chat_sessions
  async createSession(projectId: string, title?: string): Promise<ChatSession> {
    const response = await api.post<{ success: boolean; data: BackendSessionResponse }>(
      `/projects/${projectId}/chat_sessions`,
      { chat_session: { title: title || 'New Chat' } }
    );
    const session = {
      ...transformSession(response.data.data),
      projectId,
    };
    return session;
  },

  // DELETE /api/v1/projects/:project_id/chat_sessions/:id
  async deleteSession(projectId: string, sessionId: string): Promise<void> {
    await api.delete(`/projects/${projectId}/chat_sessions/${sessionId}`);
  },

  // POST /api/v1/projects/:project_id/chat_sessions/:id/stream
  // Uses fetch() for SSE streaming - relative URL goes through Vite proxy
  async sendMessage(
    projectId: string,
    sessionId: string,
    content: string,
    onChunk: (chunk: StreamMessage) => void
  ): Promise<ChatMessage> {
    const url = apiUrl(`/projects/${projectId}/chat_sessions/${sessionId}/stream`);

    const response = await fetch(
      url,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ content }),
      }
    );



    if (!response.ok) {
      const errorText = await response.text();
      console.error('[chatService.sendMessage] Error response:', errorText);
      throw new Error('Failed to send message');
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let fullContent = '';
    let buffer = ''; // Buffer for incomplete SSE data across chunks

    if (reader) {
      try {
        let done = false;
        while (!done) {
          const result = await reader.read();
          done = result.done;
          if (done) break;

          // Append decoded chunk to buffer
          buffer += decoder.decode(result.value, { stream: true });

          // Split by newlines, keeping incomplete last line in buffer
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep last incomplete line in buffer

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);

              if (data === '[DONE]') {
                onChunk({ type: 'done' });
              } else {
                try {
                  const parsed = JSON.parse(data);

                  if (parsed.content) {
                    fullContent += parsed.content;
                    onChunk({ type: 'content', content: parsed.content });
                  }
                  if (parsed.thinking) {
                    // Extended thinking / reasoning streaming
                    onChunk({ type: 'thinking', thinking: parsed.thinking });
                  }
                  if (parsed.tool_call) {
                    onChunk({
                      type: 'tool_call',
                      toolName: parsed.tool_call.name,
                      toolData: parsed.tool_call.args
                    });
                  }
                  if (parsed.tool_result) {
                    onChunk({
                      type: 'tool_result',
                      toolName: parsed.tool_result.name,
                      toolResult: parsed.tool_result.result
                    });
                  }
                  if (parsed.error) {
                    console.error('[chatService.sendMessage] Error in chunk:', parsed.error);
                    onChunk({ type: 'error', error: parsed.error });
                  }
                } catch {
                  // JSON parse failed - this shouldn't happen with proper buffering
                  // but we still skip invalid JSON gracefully
                }
              }
            }
          }
        }

        // Process any remaining data in buffer after stream ends
        if (buffer.trim() && buffer.startsWith('data: ')) {
          const data = buffer.slice(6);
          if (data !== '[DONE]') {
            try {
              const parsed = JSON.parse(data);
              if (parsed.content) {
                fullContent += parsed.content;
                onChunk({ type: 'content', content: parsed.content });
              }
            } catch {
              // Final buffer wasn't valid JSON - ignore
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    }



    return {
      id: crypto.randomUUID(),
      projectId: undefined,
      userId: 'assistant',
      content: fullContent,
      role: 'assistant',
      createdAt: new Date().toISOString(),
    };
  },

  async regenerateMessage(
    projectId: string,
    sessionId: string,
    messageId: string
  ): Promise<void> {
    await api.post(
      `/projects/${projectId}/chat_sessions/${sessionId}/messages/${messageId}/regenerate`
    );
  },

  // Global chat (no project) - direct AI engine call
  // Supports multimodal messages with images
  async sendGlobalMessage(
    sessionId: string,
    content: MessageContent,
    conversationHistory: Array<{ role: string; content: MessageContent }>,
    onChunk: (chunk: StreamMessage) => void,
    userContext?: UserContext
  ): Promise<string> {
    const messages: MultimodalMessage[] = [
      ...conversationHistory,
      { role: 'user', content },
    ];

    // Get auth token for project tools (list_projects, get_project, etc.)
    const authHeaders = getAuthHeaders() as Record<string, string>;
    const authToken = authHeaders['Authorization']
      ? authHeaders['Authorization'].replace('Bearer ', '')
      : undefined;

    const response = await fetch(aiUrl('/api/v1/chat/stream'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
      body: JSON.stringify({
        session_id: sessionId,
        messages: messages.map((m) => ({
          role: m.role,
          content: m.content,
        })),
        user_context: userContext ? {
          region: userContext.region,
          language: userContext.language,
          name: userContext.name,
          llm_preset: userContext.llmPreset,
        } : undefined,
        // Pass auth_token for project tools (list_projects, get_project, etc.)
        auth_token: authToken,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to send message to AI engine');
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let fullContent = '';
    let buffer = ''; // Buffer for incomplete SSE data across chunks

    // Helper function to process a complete line
    const processLine = (line: string) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return;

      // Extract JSON - handle "data: {...}" format
      let jsonStr = '';

      // Find the last occurrence of "data: " followed by JSON
      const dataIdx = trimmedLine.lastIndexOf('data: ');
      if (dataIdx !== -1) {
        const afterData = trimmedLine.slice(dataIdx + 6).trim();
        // Find JSON object
        const jsonStart = afterData.indexOf('{');
        if (jsonStart !== -1) {
          jsonStr = afterData.slice(jsonStart);
        } else if (afterData === '[DONE]') {
          onChunk({ type: 'done' });
          return;
        }
      } else if (trimmedLine.startsWith('{')) {
        jsonStr = trimmedLine;
      }

      if (!jsonStr) return;

      try {
        const parsed = JSON.parse(jsonStr);
        // Handle different event types from AI engine
        if (parsed.event_type === 'start') {
          // Handle start event (includes agent info for multi_agent mode)
          onChunk({
            type: 'start',
            metadata: parsed.metadata
          });
        } else if (parsed.event_type === 'token' && parsed.content) {
          fullContent += parsed.content;
          onChunk({ type: 'content', content: parsed.content });
        } else if (parsed.event_type === 'state_update' && parsed.state?.thinking) {
          // Handle extended thinking / reasoning streaming
          onChunk({
            type: 'thinking',
            thinking: parsed.state.thinking
          });
        } else if (parsed.event_type === 'tool_call') {
          // Handle tool calls (built-in or project tools)
          onChunk({
            type: 'tool_call',
            toolName: parsed.tool_name,
            toolData: parsed.tool_args || parsed.tool_data
          });
        } else if (parsed.event_type === 'tool_result') {
          // Handle tool results
          onChunk({
            type: 'tool_result',
            toolName: parsed.tool_name,
            toolResult: parsed.tool_result
          });
        } else if (parsed.event_type === 'end') {
          // Include tool_calls in done event if present
          onChunk({
            type: 'done',
            metadata: parsed.metadata
          });
        } else if (parsed.event_type === 'artifact_created') {
          // Handle artifact creation events
          onChunk({
            type: 'artifact_created',
            metadata: {
              artifact_id: parsed.artifact_id,
              artifact_name: parsed.artifact_name,
              artifact_type: parsed.artifact_type,
              file_path: parsed.artifact_file_path,
            }
          });
        } else if (parsed.event_type === 'error') {
          onChunk({ type: 'error', error: parsed.error });
        } else if (parsed.content) {
          // Fallback for simple content format
          fullContent += parsed.content;
          onChunk({ type: 'content', content: parsed.content });
        }
      } catch {
        // JSON parse failed - this shouldn't happen with proper buffering
        // but we still skip invalid JSON gracefully
      }
    };

    if (reader) {
      try {
        let done = false;
        while (!done) {
          const result = await reader.read();
          done = result.done;
          if (done) break;

          // Append decoded chunk to buffer
          buffer += decoder.decode(result.value, { stream: true });

          // Split by newlines, keeping incomplete last line in buffer
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep last incomplete line in buffer

          for (const line of lines) {
            processLine(line);
          }
        }

        // Process any remaining data in buffer after stream ends
        if (buffer.trim()) {
          processLine(buffer);
        }
      } finally {
        reader.releaseLock();
      }
    }

    return fullContent;
  },

  // ==================== Global Chat Session APIs ====================

  // GET /api/v1/chat_sessions (global sessions with pagination)
  async getGlobalSessions(params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<{
    data: ChatSession[];
    meta: {
      currentPage: number;
      totalPages: number;
      totalCount: number;
    };
  }> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('per_page', String(params.limit));
    if (params?.search) queryParams.set('search', params.search);

    const queryString = queryParams.toString();
    const url = queryString ? `/chat_sessions?${queryString}` : '/chat_sessions';

    const response = await api.get<{
      success: boolean;
      data: BackendSessionResponse[];
      meta: { current_page: number; total_pages: number; total_count: number };
    }>(url);

    return {
      data: response.data.data.map(session => transformSession(session)),
      meta: {
        currentPage: response.data.meta.current_page,
        totalPages: response.data.meta.total_pages,
        totalCount: response.data.meta.total_count,
      },
    };
  },

  // GET /api/v1/chat_sessions/:id (global session with messages)
  async getGlobalSession(sessionId: string): Promise<ChatSession> {
    const response = await api.get<{ success: boolean; data: BackendSessionWithIncluded['data']; included?: BackendMessageResponse[] }>(
      `/chat_sessions/${sessionId}`
    );

    // Extract messages from included array if present
    const messages: ChatMessage[] = [];
    if (response.data.included) {
      for (const item of response.data.included) {
        if (item.type === 'message') {
          messages.push(transformMessage(item));
        }
      }
      // Sort by createdAt
      messages.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    }

    return transformSession(response.data.data, messages);
  },

  // POST /api/v1/chat_sessions (create global session)
  async createGlobalSession(title?: string): Promise<ChatSession> {
    const response = await api.post<{ success: boolean; data: BackendSessionResponse }>(
      '/chat_sessions',
      { chat_session: { title: title || 'New Chat' } }
    );
    return transformSession(response.data.data);
  },

  // DELETE /api/v1/chat_sessions/:id (delete global session)
  async deleteGlobalSession(sessionId: string): Promise<void> {
    await api.delete(`/chat_sessions/${sessionId}`);
  },

  // POST /api/v1/chat_sessions/:id/messages (add message to global session)
  async addGlobalMessage(sessionId: string, role: string, content: string): Promise<ChatMessage> {
    const response = await api.post<{ success: boolean; data: BackendMessageResponse }>(
      `/chat_sessions/${sessionId}/messages`,
      { message: { role, content } }
    );
    return transformMessage(response.data.data);
  },
};
